import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RemoteAccessAgentStatus,
  RemoteAccessClientType,
  RemoteAccessSessionLaunch,
  SplashtopFeatureStatus,
  getRemoteAccessAgentStatus,
  getSplashtopFeature,
  startRemoteAccessSession,
} from '../components/services/remoteAccessService';

export type RemoteAccessPhase = 'idle' | 'connecting' | 'ready' | 'failed';

export interface RemoteAccessState {
  phase: RemoteAccessPhase;
  clientType: RemoteAccessClientType;
  errorCode?: string;
  errorMessage?: string;
  warning?: string;
  /** Epoch ms after which the launch data (token + session password) is no longer valid. */
  expiresAt?: number;
  /** Web client only: the browser blocked the pre-opened tab, so the user must click to open it. */
  popupBlocked: boolean;
}

const initialState: RemoteAccessState = {
  phase: 'idle',
  clientType: 'app',
  popupBlocked: false,
};

/**
 * Splashtop RMM remote access for one device page.
 * The device's agent installs the streamer and reports its state on a schedule, so there is no live connection here:
 * the page reads the tenant gate and the device status, and Connect is one POST that returns the launch data.
 * The launch data contains the session password: it lives only in a ref and is never logged or stored.
 */
export function useRemoteAccess(tenantId: string | null | undefined, agentId: string | null | undefined) {
  const [feature, setFeature] = useState<SplashtopFeatureStatus | null>(null);
  const [featureLoaded, setFeatureLoaded] = useState(false);
  const [agentStatus, setAgentStatus] = useState<RemoteAccessAgentStatus | null>(null);
  const [state, setState] = useState<RemoteAccessState>(initialState);

  const launchRef = useRef<{ launchUri?: string; webLaunchUrl?: string } | null>(null);
  const popupRef = useRef<Window | null>(null);
  // Incremented by every start/reset so a response that arrives after the user cancelled is ignored.
  const requestRef = useRef(0);

  const isRmmEnabled = feature?.enabled === true;

  // 1. Tenant gate: decides between this flow and the legacy Marketplace launch.
  useEffect(() => {
    let cancelled = false;
    setFeature(null);
    setFeatureLoaded(false);
    if (!tenantId) return;
    getSplashtopFeature(tenantId)
      .then((f) => !cancelled && setFeature(f))
      .catch(() => !cancelled && setFeature(null))
      .finally(() => !cancelled && setFeatureLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const refreshAgentStatus = useCallback(async () => {
    if (!tenantId || !agentId) return;
    try {
      setAgentStatus(await getRemoteAccessAgentStatus(tenantId, agentId));
    } catch {
      setAgentStatus(null);
    }
  }, [tenantId, agentId]);

  // 2. Device status (installed / registered / ready), only when the tenant uses Splashtop RMM.
  useEffect(() => {
    setAgentStatus(null);
    if (!isRmmEnabled) return;
    refreshAgentStatus();
  }, [isRmmEnabled, refreshAgentStatus]);

  const closePopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    popupRef.current = null;
  }, []);

  const openLaunch = useCallback(() => {
    const launch = launchRef.current;
    if (!launch) return;
    if (launch.webLaunchUrl) {
      const popup = popupRef.current && !popupRef.current.closed ? popupRef.current : window.open('', '_blank');
      if (popup) {
        popup.location.href = launch.webLaunchUrl;
        popupRef.current = null;
        setState((s) => ({ ...s, popupBlocked: false }));
      } else {
        setState((s) => ({ ...s, popupBlocked: true }));
      }
    } else if (launch.launchUri) {
      // Hands off to the Splashtop RMM viewer via its st-rmm:// protocol handler; the page stays put.
      window.location.href = launch.launchUri;
    }
  }, []);

  /** Connect click. Must be called directly from the click handler (web client pre-opens a tab to dodge popup blockers). */
  const start = useCallback(async () => {
    if (!tenantId || !agentId) return;
    const clientType: RemoteAccessClientType = agentStatus?.clientType ?? feature?.clientType ?? 'app';
    const request = ++requestRef.current;

    launchRef.current = null;
    closePopup();
    if (clientType === 'web') popupRef.current = window.open('', '_blank');
    setState({ ...initialState, phase: 'connecting', clientType });

    try {
      const launch: RemoteAccessSessionLaunch = await startRemoteAccessSession(tenantId, agentId);
      if (request !== requestRef.current) return;

      launchRef.current = { launchUri: launch.launchUri, webLaunchUrl: launch.webLaunchUrl };
      const expiresInSeconds =
        launch.clientType === 'web' ? (launch.webLaunchUrlTtlSeconds ?? launch.expiresInSeconds) : launch.expiresInSeconds;
      setState((s) => ({
        ...s,
        phase: 'ready',
        clientType: launch.clientType,
        warning: launch.warning,
        expiresAt: Date.now() + expiresInSeconds * 1000,
      }));
      openLaunch();
    } catch (err: any) {
      if (request !== requestRef.current) return;
      closePopup();
      // HTTP rejections (409/503/...) are already toasted by the api interceptor; keep the panel in sync.
      setState((s) => ({
        ...s,
        phase: 'failed',
        errorCode: err?.response?.data?.errorCode,
        errorMessage: err?.response?.data?.message || err?.message || 'Remote access could not be started.',
      }));
      refreshAgentStatus();
    }
  }, [tenantId, agentId, agentStatus?.clientType, feature?.clientType, closePopup, openLaunch, refreshAgentStatus]);

  /** Re-open the viewer / web client with the same launch data while it is still valid. */
  const relaunch = useCallback(() => {
    if (state.expiresAt && Date.now() > state.expiresAt) return;
    openLaunch();
  }, [openLaunch, state.expiresAt]);

  const reset = useCallback(() => {
    requestRef.current += 1;
    closePopup();
    launchRef.current = null;
    setState(initialState);
  }, [closePopup]);

  // Drop the launch data (session password) as soon as it expires.
  useEffect(() => {
    if (state.phase !== 'ready' || !state.expiresAt) return;
    const timer = setTimeout(
      () => {
        launchRef.current = null;
        setState((s) => ({ ...s }));
      },
      Math.max(0, state.expiresAt - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [state.phase, state.expiresAt]);

  return {
    /** Feature check finished (until then, don't pick a flow). */
    featureLoaded,
    /** Tenant uses Splashtop RMM (this flow); false → use the legacy Marketplace launch. */
    isRmmEnabled,
    feature,
    agentStatus,
    state,
    isBusy: state.phase === 'connecting',
    start,
    relaunch,
    reset,
    refreshAgentStatus,
  };
}
